// Lib
import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';

// Utils
import createSimpleClassDecorationState from '../createSimpleClassDecorationState';
import getClippingRange from './getClippingRange';
import { ActionOrigin } from '../../../actions/actionTypes';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        clipperCommands: {
            applyClippingClass: () => ReturnType;
            clearClippingClass: () => ReturnType;
            startClip: () => ReturnType;
            abortClip: () => ReturnType;
            commitClip: (transactionId: number, wasCopy: boolean) => ReturnType;
        };
    }
}

const PLUGIN_NAME = 'clipper';
const createClipperPlugin = () =>
    new Plugin({
        key: new PluginKey(PLUGIN_NAME),
        state: createSimpleClassDecorationState(PLUGIN_NAME),
        props: {
            decorations(state) {
                return this.getState(state);
            },
        },
    });

/**
 * Commands for card clipper operations.
 */
export const ClipperCommands = Extension.create({
    name: 'clipperCommands',

    addCommands() {
        return {
            applyClippingClass:
                () =>
                ({ tr, dispatch }) => {
                    if (!dispatch) return true;
                    dispatch(tr.setMeta(PLUGIN_NAME, 'clipped-text'));
                    return true;
                },
            clearClippingClass:
                () =>
                ({ tr, dispatch }) => {
                    if (!dispatch) return true;
                    dispatch(tr.setMeta(PLUGIN_NAME, ''));
                    return true;
                },
            startClip:
                () =>
                ({ editor, commands }) => {
                    editor.storage.externalStoreSynchroniser.pauseUpdates(this.name);
                    return commands.applyClippingClass();
                },
            abortClip:
                () =>
                ({ editor, commands }) => {
                    editor.storage.externalStoreSynchroniser.resumeUpdates(this.name);
                    return commands.clearClippingClass();
                },
            commitClip:
                (transactionId, wasCopy) =>
                ({ editor, commands, chain }) => {
                    editor.storage.externalStoreSynchroniser.resumeUpdates(this.name);

                    if (wasCopy) return commands.clearClippingClass();

                    const { selection } = editor.state;

                    const clippingRange = getClippingRange(selection);

                    return chain()
                        .clearClippingClass()
                        .deleteRange(clippingRange)
                        .flushPendingUpdate({ transactionId, actionOrigin: ActionOrigin.textClipper })
                        .run();
                },
        };
    },

    /**
     * Add the clipper plugin so that we can show the clipping highlight.
     */
    addProseMirrorPlugins() {
        return [createClipperPlugin()];
    },
});
